import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {InputStatusIcon} from '@/components/ui/input-status-icon/InputStatusIcon'
import {Label} from '@/components/ui/label/Label'
import {SelectItem, SelectPopover, SelectTrigger, SelectList} from '@/components/ui/select-atoms/SelectAtoms'
import {useGetInfiniteAccesses} from '@/features/accesses/queries/useGetInfiniteAccesses'
import {SelectProvider} from '@ariakit/react'
import {useTranslation} from 'react-i18next'
import {Access} from '@/features/accesses/types'
import {Text} from '@/components/ui/text/Text'
import {createDateTime, formatLocaleDate} from '@/dayjs'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'
import {HttpGetAccessesOptions} from '@/features/accesses/http/accesses.http'
import {useMemo, useState} from 'react'
import {eventTypeMapToLabel} from '@/features/agenda/utils'

type InputSelectAccessesProps = {
    label: string
    errorMessage?: string
    disabled?: boolean
    queryParams: Omit<HttpGetAccessesOptions['params'], 'limit' | 'page'>
} & (
    | {
          mode: 'single'
          value?: Access | null
          onChange: (value: Access | null) => void
      }
    | {
          mode: 'multiple'
          value?: Access[] | null
          onChange: (value: Access[] | null) => void
      }
)

export const InputSelectAccesses = ({
    label,
    value,
    disabled,
    mode,
    onChange,
    errorMessage,
    queryParams
}: InputSelectAccessesProps) => {
    const {t} = useTranslation()

    const [isQueryEnabled, setIsQueryEnabled] = useState(false)
    const {
        remappedData: accesses,
        isLoading,
        isError
    } = useGetInfiniteAccesses(
        {
            params: queryParams,
            limit: 50
        },
        {
            enabled: isQueryEnabled || !disabled
        }
    )

    const generateAccessLabel = (access: Access) => {
        const accessType = access.event.eventConsultants?.[0]?.accessType?.accessType
        return access.event.time
            ? `${formatLocaleDate(createDateTime(new Date(access.event.date), access.event.time), 'DD/MM/YYYY - HH:mm')} • ${accessType && t(eventTypeMapToLabel[accessType])}`
            : `${formatLocaleDate(new Date(access.event.date), 'DD/MM/YYYY')} • ${accessType && t(eventTypeMapToLabel[accessType])}`
    }

    // Todo: this could be a temporary solution, we should find a better way to handle this, maybe by grouping the accesses by event and then by access type from the backend
    const accessesOptions = useMemo(() => {
        const groupedAccesses = Object.entries(
            accesses.reduce(
                (acc, access) => {
                    const eventId = access.event.id
                    if (!acc[eventId]) {
                        acc[eventId] = []
                    }
                    acc[eventId].push(access)
                    return acc
                },
                {} as Record<string, Access[]>
            )
        ).map(([eventId, accesses]) => {
            const label = generateAccessLabel(accesses[0])
            return {
                label,
                value: eventId,
                accesses: accesses
            }
        })

        return groupedAccesses
    }, [accesses])

    // Get the current selected event ID based on type
    const getCurrentEventId = (): string => {
        if (mode == 'single') {
            return value?.event?.id || ''
        } else {
            return value?.[0]?.event?.id || ''
        }
    }

    // Handle value change based on type
    const handleValueChange = (eventId: string) => {
        const option = accessesOptions.find(opt => opt.value === eventId)
        if (option && option.accesses.length > 0) {
            if (mode === 'multiple') {
                onChange(option.accesses)
            } else {
                onChange(option.accesses[0])
            }
        } else {
            onChange(null)
        }
    }

    // Get display value for the trigger
    const getDisplayValue = () => {
        if (!value || (mode === 'single' && !value) || (mode === 'multiple' && value.length === 0)) return null
        return (
            <Flexbox gap={2} align="center" fullWidth>
                <Text>{generateAccessLabel(mode === 'single' ? value : value[0])}</Text>
            </Flexbox>
        )
    }

    return (
        <SelectProvider
            value={getCurrentEventId()}
            setValue={handleValueChange}
            setOpen={isOpen => {
                setIsQueryEnabled(isOpen)
            }}
        >
            <Flexbox direction="column" gap={1.5} fullWidth>
                <Label>{label}</Label>
                <SelectTrigger
                    placeholder={t('commons:select')}
                    fullWidth
                    disabled={disabled}
                    hasError={!!errorMessage}
                    displayValue={getDisplayValue}
                />
                {errorMessage && (
                    <Flexbox align="center" gap={errorMessage ? 1.5 : 0}>
                        <InputStatusIcon hasError={!!errorMessage} />
                        <InputHelpText error={errorMessage} />
                    </Flexbox>
                )}
                <SelectPopover>
                    <SelectList>
                        {isLoading ? (
                            <>
                                {Array.from({length: 6}, (_, index) => (
                                    <Skeleton key={index} height={32} />
                                ))}
                            </>
                        ) : isError ? (
                            <InputHelpText error={t('errors:default')} />
                        ) : accessesOptions.length > 0 ? (
                            accessesOptions.map(({label, value}, index) => {
                                return (
                                    <SelectItem key={index} value={value}>
                                        {label}
                                    </SelectItem>
                                )
                            })
                        ) : (
                            <InputHelpText helpText={t('commons:no_results')} />
                        )}
                    </SelectList>
                </SelectPopover>
            </Flexbox>
        </SelectProvider>
    )
}
